import { cloneDeep } from "lodash";
import memoize from "lodash.memoize";

import { OUTER, POSTCODE, SECTOR } from "../consts/layers";
import { PICKUP_PLAN_TYPE } from "../consts/planTypes";
import {
  OWNERSHIP,
  LEAVE_UNALLOCATED,
  REPLACE_ALL as REPLACE_ALL_MESSAGE,
  REPLACE_LEVEL_ONLY as REPLACE_LEVEL_ONLY_MESSAGE,
  SET_TO_ABOVE,
} from "../consts/strings";
import {
  REPLACE_ALL,
  REPLACE_LEVEL_ONLY,
  REMOVE_TO_NONE,
  REMOVE_TO_ABOVE,
} from "../consts/map";

import { groupPostcodes, groupSectors } from "./lookUpTables";
import { Postcode } from "./postcode";
import { processRoutes, processRoutesProfile } from "./routes";
import { copyOverlayDetails } from "./volumeData";

export const loadPickupType = async ({
  matrixProfile,
  routes,
  volumeData,
  lookupTables,
}) => {
  routes = processRoutes(routes, matrixProfile.routeConfig);

  const overlayData = volumeData && {
    overlayProfile: { ...volumeData.overlayProfile },
    overlayDetails: copyOverlayDetails(volumeData.overlayDetails),
  };

  const {
    routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    processedVolumeData,
  } = await processRoutesProfile({
    routeConfig: matrixProfile.routeConfig,
    redirectConfig: matrixProfile.redirectConfig,
    routes,
    volumeData: overlayData,
    overrideAreas: matrixProfile.overrideConfig,
    pickupAreas: matrixProfile.pickupConfig,
    locationConfig: matrixProfile.locationConfig,
    lookupTables,
    planType: PICKUP_PLAN_TYPE,
  });

  return {
    ...matrixProfile,
    overrideAreas: matrixProfile.overrideConfig,
    routes: routesWithVolume,
    routesByOuter,
    routesTimes,
    cutOffTimes,
    volumeData: processedVolumeData,
  };
};

export const getPickupRouteCodesMap = ({ OUTER, SECTOR, POSTCODE }) => {
  const pickupRoutes = {};
  const mapLevelConfig = (level) =>
    Object.values(level).forEach((config) =>
      Object.values(config).forEach((routeCode) => {
        pickupRoutes[routeCode] = true;
      })
    );
  mapLevelConfig(OUTER);
  mapLevelConfig(SECTOR);
  mapLevelConfig(POSTCODE);
  return pickupRoutes;
};

export const getPickupPopupLabels = memoize((pickupPartners) => {
  const pickupPartnersByType = pickupPartners.reduce((acc, partner) => {
    acc[partner.partnerType] = partner;
    return acc;
  }, {});
  const partnerTypesByKind = pickupPartners.reduce(
    (acc, { partnerType, kind }) => {
      if (!acc[kind]) acc[kind] = [partnerType];
      else acc[kind].push(partnerType);
      return acc;
    },
    {}
  );
  const partnerKindNamesByKind = pickupPartners.reduce(
    (acc, { kind, kindName }) => {
      acc[kind] = kindName;
      return acc;
    },
    {}
  );

  const joinWithAnd = (strings) => {
    if (!strings.length) return "";
    if (strings.length === 1) return strings[0];
    if (strings.length === 2) return strings.join(" and ");
    return (
      strings.slice(0, -1).join(", ") + " and " + strings[strings.length - 1]
    );
  };

  const getAllCombinations = (arr) => {
    const combinations = [];

    // The total number of non-empty subsets is 2^n - 1
    for (let i = 1; i < 1 << arr.length; i++) {
      const subset = [];
      for (let j = 0; j < arr.length; j++) {
        // Check if j-th bit is set in i
        if (i & (1 << j)) {
          subset.push(arr[j]);
        }
      }
      combinations.push(subset);
    }

    return combinations;
  };

  const getLabelSignature = (partnerTypes) => partnerTypes.join(";");

  const popupLabels = {};
  const partnerTypes = pickupPartners.map(({ partnerType }) => partnerType);
  getAllCombinations(partnerTypes).forEach((partnerTypesCombination) => {
    const kinds = Array.from(
      partnerTypesCombination.reduce((acc, partnerType) => {
        const { kind, allocateByType } = pickupPartnersByType[partnerType];
        if (!allocateByType) acc.add(kind);
        return acc;
      }, new Set())
    );
    const types = partnerTypesCombination.reduce((acc, partnerType) => {
      const { kind, allocateByType } = pickupPartnersByType[partnerType];
      if (allocateByType && !kinds.includes(kind)) acc.push(partnerType);
      return acc;
    }, []);
    const kindLabels = kinds.map((kind) => {
      const kindName = partnerKindNamesByKind[kind];
      let label = kindName;
      const exceptPartnerTypes = partnerTypesByKind[kind].filter(
        (partnerType) => !partnerTypesCombination.includes(partnerType)
      );
      if (exceptPartnerTypes.length)
        label += ` (except ${exceptPartnerTypes
          .map((partnerType) => pickupPartnersByType[partnerType].partnerName)
          .join(", ")})`;
      return label;
    });

    popupLabels[getLabelSignature(partnerTypesCombination)] = joinWithAnd([
      ...kindLabels,
      ...types.map(
        (partnerType) => pickupPartnersByType[partnerType].partnerName
      ),
    ]);
  });

  return popupLabels;
});

export const updatePickupAreas = ({
  routeCode,
  lookupTables,
  selectedPostcodes,
  pickupAreas,
  pickupUpdates,
}) => {
  pickupAreas = cloneDeep(pickupAreas);
  const level = new Postcode(selectedPostcodes[0]).getLevel();
  const selectedPostcodesByOuter = selectedPostcodes.reduce((acc, postcode) => {
    const outer = new Postcode(postcode).toOuter();
    if (!acc[outer]) acc[outer] = [];
    acc[outer].push(postcode);
    return acc;
  }, {});

  const getPartnerTypeRoute = ({ partnerType, level, key }) =>
    pickupAreas[level]?.[key]?.[partnerType] || null;
  const isPartnerTypeForAnyRoute = ({ partnerType, level, key }) =>
    !!getPartnerTypeRoute({ partnerType, level, key });
  const isPartnerTypeForEditedRoute = ({ partnerType, level, key }) =>
    getPartnerTypeRoute({ partnerType, level, key }) === routeCode;
  const isPartnerTypeForOtherRoute = ({ partnerType, level, key }) =>
    isPartnerTypeForAnyRoute({ partnerType, level, key }) &&
    getPartnerTypeRoute({ partnerType, level, key }) !== routeCode;

  const removeAnyRoutePartnerTypeAllocation = ({ partnerType, level, key }) => {
    const config = pickupAreas[level][key];
    if (!config) return;
    else if (config[partnerType]) delete config[partnerType];
    if (!Object.keys(config).length) delete pickupAreas[level][key];
  };

  const removeEditedRoutePartnerTypeAllocation = ({
    partnerType,
    level,
    key,
  }) => {
    const config = pickupAreas[level][key];
    if (!config) return;
    const configRouteCode = config[partnerType];
    if (configRouteCode === routeCode) delete config[partnerType];
    if (!Object.keys(config).length) delete pickupAreas[level][key];
  };

  const addRoutePartnerTypeAllocation = ({ partnerType, level, key }) => {
    if (!pickupAreas[level][key])
      pickupAreas[level][key] = {
        [partnerType]: routeCode,
      };
    else pickupAreas[level][key][partnerType] = routeCode;
  };

  const addOtherRoutePartnerTypeAllocation = ({
    partnerType,
    level,
    key,
    otherRouteCode,
  }) => {
    if (!pickupAreas[level][key])
      pickupAreas[level][key] = {
        [partnerType]: otherRouteCode,
      };
    else pickupAreas[level][key][partnerType] = otherRouteCode;
  };

  const consolidateOuterPartnerTypeAllocations = ({ outer, partnerType }) => {
    // Consolidate postcodes to sectors.
    const allOuterSectors = groupSectors(lookupTables.SECTOR)[outer];
    // For each sector within the outer ...
    allOuterSectors.forEach((sector) => {
      // ... get all postcodes within the sector...
      const allSectorPostcodes = groupPostcodes(lookupTables.POSTCODE)[sector];
      // ... and route allocated to partner type ...
      const firstPostcodeRouteCode = getPartnerTypeRoute({
        partnerType,
        level: POSTCODE,
        key: allSectorPostcodes[0],
      });
      // ... if every postcode within the sector for the partner type is owned by the same route ...
      if (
        !firstPostcodeRouteCode ||
        !allSectorPostcodes.every(
          (postcode) =>
            firstPostcodeRouteCode ===
            getPartnerTypeRoute({ partnerType, level: POSTCODE, key: postcode })
        )
      )
        return;
      // ... remove all postcode level partner type allocations within the sector ...
      allSectorPostcodes.forEach((postcode) =>
        removeAnyRoutePartnerTypeAllocation({
          partnerType,
          level: POSTCODE,
          key: postcode,
        })
      );
      // ... and replace it with sector level partner type allocation.
      addOtherRoutePartnerTypeAllocation({
        partnerType,
        level: SECTOR,
        key: sector,
        otherRouteCode: firstPostcodeRouteCode,
      });
    });

    // Then consolidate sectors to outer.
    // Get route owning partner type in the first sector within the outer ...
    const firstSectorRouteCode = getPartnerTypeRoute({
      partnerType,
      level: SECTOR,
      key: allOuterSectors[0],
    });
    // ... if every sector within the outer for the partner type is owned by the same route ...
    if (
      !firstSectorRouteCode ||
      !allOuterSectors.every(
        (sector) =>
          firstSectorRouteCode ===
          getPartnerTypeRoute({ partnerType, level: SECTOR, key: sector })
      )
    )
      return;
    // ... remove all sector level route allocation for partner type ...
    allOuterSectors.forEach((sector) =>
      removeAnyRoutePartnerTypeAllocation({
        partnerType,
        level: SECTOR,
        key: sector,
      })
    );
    // ... and replace it with outer level partner type allocation.
    addOtherRoutePartnerTypeAllocation({
      partnerType,
      level: OUTER,
      key: outer,
      otherRouteCode: firstSectorRouteCode,
    });
  };

  const deconsolidateOuterPartnerTypeAllocations = ({
    outer,
    partnerType,
    type,
  }) => {
    let affectedSectors = [];
    if (level === OUTER)
      affectedSectors = groupSectors(lookupTables.SECTOR)[outer];
    else if (level === SECTOR)
      affectedSectors = selectedPostcodes.filter(
        (postcode) => Postcode.toOuter(postcode) === outer
      );
    else
      affectedSectors = selectedPostcodes.reduce((acc, postcode) => {
        if (Postcode.toOuter(postcode) !== outer) return acc;
        const sector = Postcode.toSector(postcode);
        if (!acc.includes(sector)) acc.push(sector);
        return acc;
      }, []);

    const ownedPostcodes = affectedSectors.reduce((acc, sector) => {
      const sectorPostcodes = groupPostcodes(lookupTables.POSTCODE)[sector];
      sectorPostcodes.forEach((postcode) => {
        if (
          !isPartnerTypeForAnyRoute({
            partnerType,
            level: POSTCODE,
            key: postcode,
          }) &&
          !isPartnerTypeForAnyRoute({
            partnerType,
            level: SECTOR,
            key: Postcode.toSector(postcode),
          })
        )
          isPartnerTypeForEditedRoute({
            partnerType,
            level: OUTER,
            key: Postcode.toOuter(postcode),
          }) && acc.push(postcode);
        else if (
          !isPartnerTypeForAnyRoute({
            partnerType,
            level: POSTCODE,
            key: postcode,
          })
        )
          isPartnerTypeForEditedRoute({
            partnerType,
            level: SECTOR,
            key: Postcode.toSector(postcode),
          }) && acc.push(postcode);
        else
          isPartnerTypeForEditedRoute({
            partnerType,
            level: POSTCODE,
            key: postcode,
          }) && acc.push(postcode);
      });
      return acc;
    }, []);

    const deconsolidateOuter = (
      type === REMOVE_TO_NONE
        ? isPartnerTypeForAnyRoute
        : isPartnerTypeForEditedRoute
    )({
      partnerType,
      level: OUTER,
      key: outer,
    });
    const deconsolidationSectors = Array.from(
      ownedPostcodes.reduce((acc, postcode) => {
        const sector = Postcode.toSector(postcode);
        if (
          (deconsolidateOuter &&
            (type === REMOVE_TO_NONE ||
              !isPartnerTypeForOtherRoute({
                partnerType,
                level: SECTOR,
                key: sector,
              }))) ||
          (type === REMOVE_TO_NONE
            ? isPartnerTypeForAnyRoute
            : isPartnerTypeForEditedRoute)({
            partnerType,
            level: SECTOR,
            key: sector,
          })
        )
          acc.add(sector);
        return acc;
      }, new Set())
    );
    deconsolidationSectors.forEach((sector) => {
      const otherRouteCode =
        getPartnerTypeRoute({
          partnerType,
          level: SECTOR,
          key: sector,
        }) ||
        getPartnerTypeRoute({
          partnerType,
          level: OUTER,
          key: outer,
        });
      removeAnyRoutePartnerTypeAllocation({
        partnerType,
        level: SECTOR,
        key: sector,
      });
      groupPostcodes(lookupTables.POSTCODE)[sector].forEach((postcode) => {
        if (
          !isPartnerTypeForAnyRoute({
            partnerType,
            level: POSTCODE,
            key: postcode,
          })
        )
          addOtherRoutePartnerTypeAllocation({
            partnerType,
            level: POSTCODE,
            key: postcode,
            otherRouteCode,
          });
      });
    });

    if (deconsolidateOuter) {
      const otherRouteCode = getPartnerTypeRoute({
        partnerType,
        level: OUTER,
        key: outer,
      });
      groupSectors(lookupTables.SECTOR)[outer].forEach((sector) => {
        if (
          !deconsolidationSectors.includes(sector) &&
          !(
            type === REMOVE_TO_NONE
              ? isPartnerTypeForAnyRoute
              : isPartnerTypeForOtherRoute
          )({
            partnerType,
            level: SECTOR,
            key: sector,
          })
        )
          addOtherRoutePartnerTypeAllocation({
            partnerType,
            level: SECTOR,
            key: sector,
            otherRouteCode,
          });
      });
      removeAnyRoutePartnerTypeAllocation({
        partnerType,
        level: OUTER,
        key: outer,
      });
    }
  };

  const outerUpdatePickupAreas = () =>
    pickupUpdates.forEach(({ key: partnerType, type }) => {
      selectedPostcodes.forEach((outer) => {
        const outerSectors = groupSectors(lookupTables.SECTOR)[outer];
        const outerPostcodes = outerSectors.flatMap(
          (sector) => groupPostcodes(lookupTables.POSTCODE)[sector]
        );
        if ([REPLACE_ALL, REPLACE_LEVEL_ONLY].includes(type)) {
          // Remove all the postcode level partner type allocations for:
          // - any route if replacing all allocations
          // - edited route if replacing only level allocations, unless it has sector level allocation above
          outerPostcodes.forEach((postcode) => {
            if (
              type === REPLACE_LEVEL_ONLY &&
              isPartnerTypeForOtherRoute({
                partnerType,
                level: SECTOR,
                key: Postcode.toSector(postcode),
              })
            )
              return;
            (type === REPLACE_ALL
              ? removeAnyRoutePartnerTypeAllocation
              : removeEditedRoutePartnerTypeAllocation)({
              partnerType,
              level: POSTCODE,
              key: postcode,
            });
          });
          // Remove all the sector level partner type allocations for:
          // - any route if replacing all allocations
          // - edited route if replacing only level allocations
          outerSectors.forEach((sector) =>
            (type === REPLACE_ALL
              ? removeAnyRoutePartnerTypeAllocation
              : removeEditedRoutePartnerTypeAllocation)({
              partnerType,
              level: SECTOR,
              key: sector,
            })
          );
          // Allocated entirety of outer to the route
          addRoutePartnerTypeAllocation({
            partnerType,
            level: OUTER,
            key: outer,
          });
          // Consolidate partner type allocations after updates
          consolidateOuterPartnerTypeAllocations({
            partnerType,
            outer,
          });
        } else if ([REMOVE_TO_NONE, REMOVE_TO_ABOVE].includes(type)) {
          deconsolidateOuterPartnerTypeAllocations({
            outer,
            partnerType,
            type,
          });

          // Remove postcode level partner type allocations for the route
          outerPostcodes.forEach((postcode) =>
            removeEditedRoutePartnerTypeAllocation({
              partnerType,
              level: POSTCODE,
              key: postcode,
            })
          );
          // Remove sector level partner type allocations for the route
          outerSectors.forEach((sector) =>
            removeEditedRoutePartnerTypeAllocation({
              partnerType,
              level: SECTOR,
              key: sector,
            })
          );
          // Remove outer level partner type allocation for the route
          removeEditedRoutePartnerTypeAllocation({
            partnerType,
            level: OUTER,
            key: outer,
          });
        }
      });
    });

  const sectorUpdatePickupAreas = () =>
    pickupUpdates.forEach(({ key: partnerType, type }) => {
      Object.entries(selectedPostcodesByOuter).forEach(
        ([outer, outerSectors]) => {
          if ([REMOVE_TO_NONE, REMOVE_TO_ABOVE].includes(type))
            deconsolidateOuterPartnerTypeAllocations({
              outer,
              partnerType,
              type,
            });

          outerSectors.forEach((sector) => {
            const sectorPostcodes = groupPostcodes(lookupTables.POSTCODE)[
              sector
            ];
            if ([REPLACE_ALL, REPLACE_LEVEL_ONLY].includes(type)) {
              // Remove all the postcode level partner type allocations for:
              // - any route if replacing all allocations
              // - edited route if replacing only level allocations
              sectorPostcodes.forEach((postcode) =>
                (type === REPLACE_ALL
                  ? removeAnyRoutePartnerTypeAllocation
                  : removeEditedRoutePartnerTypeAllocation)({
                  partnerType,
                  level: POSTCODE,
                  key: postcode,
                })
              );

              // If entirety of outer partner type is owned by the route already ...
              if (
                isPartnerTypeForEditedRoute({
                  partnerType,
                  level: OUTER,
                  key: outer,
                })
              )
                // ... remove sector level partner type allocation ...
                removeAnyRoutePartnerTypeAllocation({
                  partnerType,
                  level: SECTOR,
                  key: sector,
                });
              // ... otherwise allocate entirety of sector to the route
              else
                addRoutePartnerTypeAllocation({
                  partnerType,
                  level: SECTOR,
                  key: sector,
                });
            } else if ([REMOVE_TO_NONE, REMOVE_TO_ABOVE].includes(type)) {
              // Remove postcode level partner type allocations for the route
              sectorPostcodes.forEach((postcode) =>
                removeEditedRoutePartnerTypeAllocation({
                  partnerType,
                  level: POSTCODE,
                  key: postcode,
                })
              );
              // Remove sector level partner type allocation for the route
              removeEditedRoutePartnerTypeAllocation({
                partnerType,
                level: SECTOR,
                key: sector,
              });
            }
          });
          // Consolidate partner type allocations after updates
          if ([REPLACE_ALL, REPLACE_LEVEL_ONLY].includes(type))
            consolidateOuterPartnerTypeAllocations({
              outer,
              partnerType,
            });
        }
      );
    });

  const postcodeUpdatePickupAreas = () =>
    pickupUpdates.forEach(({ key: partnerType, type }) => {
      Object.entries(selectedPostcodesByOuter).forEach(
        ([outer, outerPostcodes]) => {
          if ([REMOVE_TO_NONE, REMOVE_TO_ABOVE].includes(type))
            deconsolidateOuterPartnerTypeAllocations({
              outer,
              partnerType,
              type,
            });

          outerPostcodes.forEach((postcode) => {
            const sector = new Postcode(postcode).toSector();
            if ([REPLACE_ALL, REPLACE_LEVEL_ONLY].includes(type)) {
              // If whole of sector ...
              if (
                isPartnerTypeForEditedRoute({
                  partnerType,
                  level: SECTOR,
                  key: sector,
                }) ||
                // ... or outer is owned by the route already ...
                (!isPartnerTypeForOtherRoute({
                  partnerType,
                  level: SECTOR,
                  key: sector,
                }) &&
                  isPartnerTypeForEditedRoute({
                    partnerType,
                    level: OUTER,
                    key: outer,
                  }))
              )
                // ... remove partner type postcode level allocation ...
                removeAnyRoutePartnerTypeAllocation({
                  partnerType,
                  level: POSTCODE,
                  key: postcode,
                });
              // ... oterwise allocate the postcode partner type to the route
              else
                addRoutePartnerTypeAllocation({
                  partnerType,
                  level: POSTCODE,
                  key: postcode,
                });
            } else if ([REMOVE_TO_ABOVE, REMOVE_TO_NONE].includes(type)) {
              // Remove the postcode level partner type allocation for the route
              removeEditedRoutePartnerTypeAllocation({
                partnerType,
                level: POSTCODE,
                key: postcode,
              });
            }
          });

          // Consolidate partner type allocations after updates
          if ([REPLACE_ALL, REPLACE_LEVEL_ONLY].includes(type))
            consolidateOuterPartnerTypeAllocations({
              outer,
              partnerType,
            });
        }
      );
    });

  if (level === OUTER) outerUpdatePickupAreas();
  else if (level === SECTOR) sectorUpdatePickupAreas();
  else postcodeUpdatePickupAreas();

  return pickupAreas;
};

export const getPickupToSelectedPostcodesByType = ({
  lookupTables,
  pickupPartners,
  pickupAreas,
  selectedPostcodes,
}) => {
  const pickupToPostcodeByType = getPickupToPostcodeByType({
    lookupTables,
    pickupPartners,
    pickupAreas,
  });

  const pickupToSelectedPostcodeByType = {};
  const level = new Postcode(selectedPostcodes[0]).getLevel();
  selectedPostcodes = [...selectedPostcodes].sort();

  selectedPostcodes.forEach((p) => {
    Object.keys(pickupToPostcodeByType).forEach((type) => {
      if (!pickupToSelectedPostcodeByType[type])
        pickupToSelectedPostcodeByType[type] = {};

      const pickupToPostcodeForType = pickupToPostcodeByType[type];
      const postcode = new Postcode(p);
      let config;
      if (level === POSTCODE)
        config =
          pickupToPostcodeForType.POSTCODE[postcode.getPostcode(POSTCODE)]?.own;

      if (level === SECTOR)
        config =
          pickupToPostcodeForType.SECTOR[postcode.getPostcode(SECTOR)]?.own;
      else if (level === POSTCODE && !config)
        config =
          pickupToPostcodeForType.SECTOR[postcode.getPostcode(SECTOR)]
            ?.inherited;

      if (level === OUTER)
        config =
          pickupToPostcodeForType.OUTER[postcode.getPostcode(OUTER)]?.own;
      else if (!config)
        config =
          pickupToPostcodeForType.OUTER[postcode.getPostcode(OUTER)]?.inherited;

      pickupToSelectedPostcodeByType[type][p] = config || {
        configByRouteCode: {},
        popupLabels: {},
        outerRouteCodes: [null],
        sectorRouteCodes: [],
        postcodeRouteCodes: [],
        routeCodes: [null],
      };
    });
  });

  return Object.keys(pickupToPostcodeByType).reduce((acc, type) => {
    const typeConfig = pickupToSelectedPostcodeByType[type];
    const configByRouteCode = {};
    const popupLabels = {};
    let outerRouteCodes = new Set();
    let sectorRouteCodes = new Set();
    let postcodeRouteCodes = new Set();
    let routeCodes = new Set();
    Object.values(typeConfig).forEach(
      ({
        configByRouteCode: typeConfigByRouteCode,
        popupLabels: typePopupLabels,
        outerRouteCodes: typeOuterRouteCodes,
        sectorRouteCodes: typeSectorRouteCodes,
        postcodeRouteCodes: typePostcodeRouteCodes,
      }) => {
        Object.entries(typeConfigByRouteCode).forEach(
          ([routeCode, routeConfig]) => {
            if (!configByRouteCode[routeCode])
              configByRouteCode[routeCode] = {};
            Object.entries(routeConfig).forEach(([key, value]) => {
              configByRouteCode[routeCode][key] = value;
            });
          }
        );
        Object.entries(typePopupLabels).forEach(([routeCode, routeLabels]) => {
          if (!popupLabels[routeCode]) popupLabels[routeCode] = {};
          Object.entries(routeLabels).forEach(([key, value]) => {
            popupLabels[routeCode][key] = value;
          });
        });
        typeOuterRouteCodes.forEach((routeCode) =>
          outerRouteCodes.add(routeCode)
        );
        typeSectorRouteCodes.forEach((routeCode) =>
          sectorRouteCodes.add(routeCode)
        );
        typePostcodeRouteCodes.forEach((routeCode) =>
          sectorRouteCodes.add(routeCode)
        );
      }
    );
    outerRouteCodes = Array.from(outerRouteCodes);
    sectorRouteCodes = Array.from(sectorRouteCodes);
    postcodeRouteCodes = Array.from(postcodeRouteCodes);
    outerRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));
    sectorRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));
    postcodeRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));
    routeCodes = Array.from(routeCodes);
    acc[type] = {
      popupLabels,
      outerRouteCodes,
      sectorRouteCodes,
      postcodeRouteCodes,
      routeCodes,
    };
    return acc;
  }, {});
};

export const getPickupToPostcodeByType = ({
  lookupTables,
  pickupPartners,
  pickupAreas,
}) => {
  const popupLabels = getPickupPopupLabels(pickupPartners);
  const pickupPartnersByKind = {};
  pickupPartners.forEach(({ partnerType, partnerName, kind }) => {
    if (!pickupPartnersByKind[kind])
      pickupPartnersByKind[kind] = [{ partnerType, partnerName, kind }];
    else pickupPartnersByKind[kind].push({ partnerType, partnerName, kind });
  });

  const pickupPostcodeByType = {};
  Object.entries(pickupPartnersByKind).forEach(([kind, kindPickupPartners]) => {
    pickupPostcodeByType[kind] = getPickupToPostcode({
      lookupTables,
      pickupPartners: kindPickupPartners,
      pickupAreas,
      popupLabels,
    });
  });
  pickupPartners.forEach((partner) => {
    pickupPostcodeByType[partner.partnerType] = getPickupToPostcode({
      lookupTables,
      pickupPartners: [partner],
      pickupAreas,
      popupLabels,
    });
  });

  return pickupPostcodeByType;
};

export const getPickupToPostcode = ({
  lookupTables,
  pickupPartners,
  pickupAreas,
  popupLabels,
}) => {
  if (!lookupTables || !pickupPartners || !pickupAreas) return null;

  const pickupPostcodeByOuter = {};
  const pickupPostcodeBySector = {};
  const pickupSectorByOuter = {};

  const pickupPostcodeConfig = {};
  const pickupSectorConfig = {};
  const pickupOuterConfig = {};

  const getPopupLabelSignature = (...configs) =>
    pickupPartners
      .reduce((acc, { partnerType }) => {
        if (configs.find((config) => !!config[partnerType]))
          acc.push(partnerType);
        return acc;
      }, [])
      .join(";");

  const getPopupLabels = (configByRouteCode) =>
    Object.entries(configByRouteCode).reduce(
      (acc, [routeCode, routeConfig]) => {
        acc[routeCode] = {};
        Object.entries(routeConfig).forEach(([postcode, postcodeConfig]) => {
          const configLevel = Postcode.getLevel(postcode);
          let popupLabelSignature;
          if (configLevel === OUTER)
            popupLabelSignature = getPopupLabelSignature(postcodeConfig);
          else if (configLevel === SECTOR) {
            const outerLabelSignature =
              routeConfig[Postcode.toOuter(postcode)] || {};
            popupLabelSignature = getPopupLabelSignature(
              outerLabelSignature,
              postcodeConfig
            );
          } else {
            const outerLabelSignature =
              routeConfig[Postcode.toOuter(postcode)] || {};
            const sectorLabelSignature =
              routeConfig[Postcode.toSector(postcode)] || {};
            popupLabelSignature = getPopupLabelSignature(
              outerLabelSignature,
              sectorLabelSignature,
              postcodeConfig
            );
          }
          acc[routeCode][postcode] = popupLabels[popupLabelSignature];
        });
        return acc;
      },
      {}
    );

  const assignPartnerTypeToRoute = ({
    configByRouteCode,
    routeCode,
    postcode,
    partnerType,
  }) => {
    if (!configByRouteCode[routeCode]) configByRouteCode[routeCode] = {};
    if (!configByRouteCode[routeCode][postcode])
      configByRouteCode[routeCode][postcode] = {};
    configByRouteCode[routeCode][postcode][partnerType] = true;
  };

  Object.keys(pickupAreas.POSTCODE).forEach((postcode) => {
    const p = new Postcode(postcode);
    const outer = p.toOuter();
    const sector = p.toSector();

    const postcodeConfig = {};
    const sectorConfig = {};
    const outerConfig = {};

    const configByRouteCode = {};
    let outerRouteCodes = new Set();
    let sectorRouteCodes = new Set();
    let postcodeRouteCodes = new Set();
    pickupPartners.forEach(({ partnerType }) => {
      if (pickupAreas.POSTCODE[postcode]?.[partnerType])
        postcodeConfig[partnerType] =
          pickupAreas.POSTCODE[postcode][partnerType];
      if (pickupAreas.SECTOR[sector]?.[partnerType])
        sectorConfig[partnerType] = pickupAreas.SECTOR[sector][partnerType];
      if (pickupAreas.OUTER[outer]?.[partnerType])
        outerConfig[partnerType] = pickupAreas.OUTER[outer][partnerType];

      const postcodeRouteCode = postcodeConfig[partnerType];
      const sectorRouteCode = sectorConfig[partnerType];
      const outerRouteCode = outerConfig[partnerType];

      if (postcodeRouteCode) {
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode: postcodeRouteCode,
          postcode,
          partnerType,
        });
        postcodeRouteCodes.add(postcodeRouteCode);
      } else if (sectorRouteCode) {
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode: sectorRouteCode,
          postcode: sector,
          partnerType,
        });
        sectorRouteCodes.add(sectorRouteCode);
      } else if (outerRouteCode) {
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode: outerRouteCode,
          postcode: outer,
          partnerType,
        });
        outerRouteCodes.add(outerRouteCode);
      } else outerRouteCodes.add(null);
    });
    if (!Object.keys(postcodeConfig).length) return;

    const routeCodes = new Set();
    outerRouteCodes = Array.from(outerRouteCodes);
    sectorRouteCodes = Array.from(sectorRouteCodes);
    postcodeRouteCodes = Array.from(postcodeRouteCodes);

    outerRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));
    sectorRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));
    postcodeRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));

    pickupPostcodeConfig[postcode] = {
      own: {
        config: {
          [outer]: outerConfig,
          [sector]: sectorConfig,
          [postcode]: postcodeConfig,
        },
        configByRouteCode,
        popupLabels: getPopupLabels(configByRouteCode),
        outerRouteCodes,
        sectorRouteCodes,
        postcodeRouteCodes,
        routeCodes: Array.from(routeCodes),
      },
    };

    if (!pickupPostcodeByOuter[outer]) pickupPostcodeByOuter[outer] = {};
    pickupPostcodeByOuter[outer][postcode] = postcodeConfig;
    if (!pickupPostcodeBySector[sector]) pickupPostcodeBySector[sector] = {};
    pickupPostcodeBySector[sector][postcode] = postcodeConfig;
  });

  Object.keys(lookupTables.SECTOR).forEach((sector) => {
    const outer = new Postcode(sector).toOuter();

    const postcodeConfigs = pickupPostcodeBySector[sector] || {};
    const sectorConfig = {};
    const outerConfig = {};

    const configByRouteCode = {};
    let outerRouteCodes = new Set();
    let sectorRouteCodes = new Set();
    let postcodeRouteCodes = new Set();
    pickupPartners.forEach(({ partnerType }) => {
      if (pickupAreas.SECTOR[sector]?.[partnerType])
        sectorConfig[partnerType] = pickupAreas.SECTOR[sector][partnerType];
      if (pickupAreas.OUTER[outer]?.[partnerType])
        outerConfig[partnerType] = pickupAreas.OUTER[outer][partnerType];
      const sectorRouteCode = sectorConfig[partnerType];
      const outerRouteCode = outerConfig[partnerType];

      if (sectorRouteCode) {
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode: sectorRouteCode,
          postcode: sector,
          partnerType,
        });
        sectorRouteCodes.add(sectorRouteCode);
      } else if (outerRouteCode) {
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode: outerRouteCode,
          postcode: outer,
          partnerType,
        });
        outerRouteCodes.add(outerRouteCode);
      } else outerRouteCodes.add(null);
    });

    const routeCodes = new Set();
    outerRouteCodes = Array.from(outerRouteCodes);
    sectorRouteCodes = Array.from(sectorRouteCodes);

    outerRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));
    sectorRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));

    const inherited = {
      config: {
        [outer]: outerConfig,
        [sector]: sectorConfig,
      },
      configByRouteCode: cloneDeep(configByRouteCode),
      popupLabels: getPopupLabels(configByRouteCode),
      outerRouteCodes,
      sectorRouteCodes,
      postcodeRouteCodes: [],
      routeCodes: Array.from(routeCodes),
    };
    if (
      !Object.keys(sectorConfig).length &&
      !Object.keys(postcodeConfigs).length
    )
      return;

    pickupPartners.forEach(({ partnerType }) => {
      Object.entries(postcodeConfigs).forEach(([postcode, postcodeConfig]) => {
        const routeCode = postcodeConfig[partnerType];
        if (!routeCode) return;
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode,
          postcode,
          partnerType,
        });
        postcodeRouteCodes.add(routeCode);
      });
    });

    postcodeRouteCodes = Array.from(postcodeRouteCodes);
    postcodeRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));

    pickupSectorConfig[sector] = {
      inherited,
      own: {
        config: {
          [outer]: outerConfig,
          [sector]: sectorConfig,
          ...postcodeConfigs,
        },
        configByRouteCode,
        popupLabels: getPopupLabels(configByRouteCode),
        outerRouteCodes,
        sectorRouteCodes,
        postcodeRouteCodes,
        routeCodes: Array.from(routeCodes),
      },
    };

    if (!pickupSectorByOuter[outer]) pickupSectorByOuter[outer] = {};
    pickupSectorByOuter[outer][sector] = sectorConfig;
  });

  Object.keys(lookupTables.OUTER).forEach((outer) => {
    const postcodeConfigs = pickupPostcodeByOuter[outer] || {};
    const sectorConfigs = pickupSectorByOuter[outer] || {};
    const outerConfig = {};

    const configByRouteCode = {};
    let outerRouteCodes = new Set();
    let sectorRouteCodes = new Set();
    let postcodeRouteCodes = new Set();
    pickupPartners.forEach(({ partnerType }) => {
      if (pickupAreas.OUTER[outer]?.[partnerType])
        outerConfig[partnerType] = pickupAreas.OUTER[outer][partnerType];
      const routeCode = outerConfig[partnerType];

      if (routeCode) {
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode,
          postcode: outer,
          partnerType,
        });
        outerRouteCodes.add(routeCode);
      } else outerRouteCodes.add(null);
    });
    if (
      !Object.keys(outerConfig).length &&
      !Object.keys(sectorConfigs).length &&
      !Object.keys(postcodeConfigs).length
    )
      return;

    const routeCodes = new Set();
    outerRouteCodes = Array.from(outerRouteCodes);
    outerRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));

    const inherited = {
      config: {
        [outer]: outerConfig,
      },
      configByRouteCode: cloneDeep(configByRouteCode),
      popupLabels: getPopupLabels(configByRouteCode),
      outerRouteCodes,
      sectorRouteCodes: Array.from(sectorRouteCodes),
      postcodeRouteCodes: Array.from(postcodeRouteCodes),
      routeCodes: Array.from(routeCodes),
    };

    pickupPartners.forEach(({ partnerType }) => {
      Object.entries(sectorConfigs).forEach(([sector, sectorConfig]) => {
        const routeCode = sectorConfig[partnerType];
        if (!routeCode) return;
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode,
          postcode: sector,
          partnerType,
        });
        sectorRouteCodes.add(routeCode);
      });
    });
    pickupPartners.forEach(({ partnerType }) => {
      Object.entries(postcodeConfigs).forEach(([postcode, postcodeConfig]) => {
        const routeCode = postcodeConfig[partnerType];
        if (!routeCode) return;
        assignPartnerTypeToRoute({
          configByRouteCode,
          routeCode,
          postcode,
          partnerType,
        });
        postcodeRouteCodes.add(routeCode);
      });
    });

    sectorRouteCodes = Array.from(sectorRouteCodes);
    sectorRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));
    postcodeRouteCodes = Array.from(postcodeRouteCodes);
    postcodeRouteCodes.forEach((routeCode) => routeCodes.add(routeCode));

    pickupOuterConfig[outer] = {
      inherited,
      own: {
        config: {
          [outer]: outerConfig,
          ...sectorConfigs,
          ...postcodeConfigs,
        },
        configByRouteCode,
        popupLabels: getPopupLabels(configByRouteCode),
        outerRouteCodes,
        sectorRouteCodes,
        postcodeRouteCodes,
        routeCodes: Array.from(routeCodes),
      },
    };
  });

  return {
    OUTER: pickupOuterConfig,
    SECTOR: pickupSectorConfig,
    POSTCODE: pickupPostcodeConfig,
  };
};

export const getPickupModalConfig = ({
  lookupTables,
  pickupPartners,
  pickupAreas,
  routeInEditCode,
  selectedPostcodes,
  pickupToSelectedPostcodesByType,
}) => {
  const level = Postcode.getLevel(selectedPostcodes[0]);
  const sectorsByOuter = groupSectors(lookupTables.SECTOR);
  const postcodesBySector = groupPostcodes(lookupTables.POSTCODE);
  let editPostcodes;
  if (level === OUTER)
    editPostcodes = selectedPostcodes
      .flatMap((outer) => sectorsByOuter[outer])
      .flatMap((sector) => postcodesBySector[sector]);
  else if (level === SECTOR)
    editPostcodes = selectedPostcodes.flatMap(
      (sector) => postcodesBySector[sector]
    );
  else editPostcodes = selectedPostcodes;

  const getPartnerTypeDetails = ({ partnerType }) => {
    let postcodesForInEditRoute = 0;
    let aboveLevelForOtherRoute = false;
    let lowerLevelForOtherRoute = false;
    editPostcodes.forEach((postcode) => {
      const outer = new Postcode(postcode).toOuter();
      const sector = new Postcode(postcode).toSector();

      const postcodeConfig = pickupAreas.POSTCODE[postcode] || {
        [partnerType]: null,
      };
      const sectorConfig = pickupAreas.SECTOR[sector] || {
        [partnerType]: null,
      };
      const outerConfig = pickupAreas.OUTER[outer] || { [partnerType]: null };

      const postcodeLevelRouteCode = postcodeConfig[partnerType];
      const sectorLevelRouteCode = sectorConfig[partnerType];
      const outerLevelRouteCode = outerConfig[partnerType];

      const postcodeRouteCode =
        postcodeLevelRouteCode || sectorLevelRouteCode || outerLevelRouteCode;

      if (postcodeRouteCode === routeInEditCode) postcodesForInEditRoute++;

      if (
        level === OUTER &&
        sectorLevelRouteCode &&
        sectorLevelRouteCode !== routeInEditCode
      )
        lowerLevelForOtherRoute = true;
      else if (
        level !== POSTCODE &&
        postcodeLevelRouteCode &&
        postcodeLevelRouteCode !== routeInEditCode
      )
        lowerLevelForOtherRoute = true;

      if (
        postcodeLevelRouteCode === routeInEditCode &&
        sectorLevelRouteCode &&
        sectorLevelRouteCode !== routeInEditCode
      )
        aboveLevelForOtherRoute = true;
      else if (
        postcodeLevelRouteCode === routeInEditCode &&
        !sectorLevelRouteCode &&
        outerLevelRouteCode &&
        outerLevelRouteCode !== routeInEditCode
      )
        aboveLevelForOtherRoute = true;
      else if (
        sectorLevelRouteCode === routeInEditCode &&
        outerLevelRouteCode &&
        outerLevelRouteCode !== routeInEditCode
      )
        aboveLevelForOtherRoute = true;
    });
    let value;
    if (editPostcodes.length === postcodesForInEditRoute) value = OWNERSHIP.ALL;
    else if (postcodesForInEditRoute > 0) value = OWNERSHIP.SOME;
    else value = OWNERSHIP.NONE;

    return {
      value,
      aboveLevelForOtherRoute,
      lowerLevelForOtherRoute,
    };
  };

  const editModalPickupKinds = {};
  const editModalPickupPartners = {};

  pickupPartners.forEach(
    ({ partnerType, partnerName, kind, kindName, allocateByType }) => {
      const { value, aboveLevelForOtherRoute, lowerLevelForOtherRoute } =
        getPartnerTypeDetails({ partnerType });

      if (!editModalPickupKinds[kind])
        editModalPickupKinds[kind] = {
          key: kind,
          label: kindName,
          value: null,
          aboveLevelForOtherRoute,
          lowerLevelForOtherRoute,
          kind,
          isKind: true,
          types: [],
          config: pickupToSelectedPostcodesByType[kind],
        };

      if (editModalPickupKinds[kind].value === null)
        editModalPickupKinds[kind].value = value;
      else if (editModalPickupKinds[kind].value !== value)
        editModalPickupKinds[kind].value = OWNERSHIP.SOME;

      if (aboveLevelForOtherRoute)
        editModalPickupKinds[kind].aboveLevelForOtherRoute = true;
      if (lowerLevelForOtherRoute)
        editModalPickupKinds[kind].lowerLevelForOtherRoute = true;

      editModalPickupKinds[kind].types.push(partnerType);

      editModalPickupPartners[partnerType] = {
        key: partnerType,
        label: partnerName,
        kind,
        value,
        aboveLevelForOtherRoute,
        lowerLevelForOtherRoute,
        hide: !allocateByType,
        config: pickupToSelectedPostcodesByType[partnerType],
      };
    }
  );

  const pickupModalConfig = [];
  Object.values(editModalPickupKinds).forEach((pickupModalKindConfig) => {
    pickupModalConfig.push(pickupModalKindConfig);
    Object.values(editModalPickupPartners).forEach(
      (pickupModalPartnerConfig) => {
        if (pickupModalPartnerConfig.kind === pickupModalKindConfig.kind)
          pickupModalConfig.push(pickupModalPartnerConfig);
      }
    );
  });
  return pickupModalConfig;
};

export const getPickupUpdateOptions = ({ key, pickupModalConfig }) => {
  const configToUpdate = pickupModalConfig.find((config) => config.key === key);
  const { aboveLevelForOtherRoute, lowerLevelForOtherRoute } = configToUpdate;

  const primaryActionType =
    configToUpdate.value === OWNERSHIP.ALL ? REMOVE_TO_NONE : REPLACE_ALL;
  const primaryActionMessage =
    primaryActionType === REPLACE_ALL ? REPLACE_ALL_MESSAGE : LEAVE_UNALLOCATED;

  let secondaryActionType = null;
  let secondaryActionMessage = null;
  if (primaryActionType === REPLACE_ALL && lowerLevelForOtherRoute) {
    secondaryActionType = REPLACE_LEVEL_ONLY;
    secondaryActionMessage = REPLACE_LEVEL_ONLY_MESSAGE;
  } else if (primaryActionType === REMOVE_TO_NONE && aboveLevelForOtherRoute) {
    secondaryActionType = REMOVE_TO_ABOVE;
    secondaryActionMessage = SET_TO_ABOVE;
  }

  let tertiaryActionType = null;
  let tertiaryActionMessage = null;
  if (configToUpdate.value === OWNERSHIP.SOME) {
    tertiaryActionType = REMOVE_TO_NONE;
    tertiaryActionMessage = LEAVE_UNALLOCATED;
  }

  let quaternaryActionType = null;
  let quaternaryActionMessage = null;
  if (configToUpdate.value === OWNERSHIP.SOME && aboveLevelForOtherRoute) {
    quaternaryActionType = REMOVE_TO_ABOVE;
    quaternaryActionMessage = SET_TO_ABOVE;
  }

  const primaryPickupUpdates = [];
  const secondaryPickupUpdates = [];
  const tertiaryPickupUpdates = [];
  const quaternaryPickupUpdates = [];
  (configToUpdate.isKind ? configToUpdate.types : [key]).forEach((key) => {
    primaryPickupUpdates.push({ key, type: primaryActionType });
    secondaryActionType &&
      secondaryPickupUpdates.push({ key, type: secondaryActionType });
    tertiaryActionType &&
      tertiaryPickupUpdates.push({ key, type: tertiaryActionType });
    quaternaryActionType &&
      quaternaryPickupUpdates.push({ key, type: quaternaryActionType });
  });

  const updateOptions = [
    {
      message: primaryActionMessage,
      updates: primaryPickupUpdates,
    },
  ];

  if (secondaryActionType)
    updateOptions.push({
      message: secondaryActionMessage,
      updates: secondaryPickupUpdates,
    });
  if (tertiaryActionType)
    updateOptions.push({
      message: tertiaryActionMessage,
      updates: tertiaryPickupUpdates,
    });
  if (quaternaryActionType)
    updateOptions.push({
      message: quaternaryActionMessage,
      updates: quaternaryPickupUpdates,
    });

  return updateOptions;
};

export const getUpdatedPickupModal = ({
  lookupTables,
  pickupPartners,
  routeInEditCode,
  selectedPostcodes,
  pickupAreas,
  updateOption,
}) => {
  const { updates: pickupUpdates } = updateOption;

  const newPickupAreas = updatePickupAreas({
    routeCode: routeInEditCode,
    lookupTables,
    selectedPostcodes,
    pickupAreas,
    pickupUpdates,
  });
  const pickupToSelectedPostcodesByType = getPickupToSelectedPostcodesByType({
    lookupTables,
    pickupPartners,
    pickupAreas: newPickupAreas,
    selectedPostcodes,
  });
  const newPickupModalConfig = getPickupModalConfig({
    lookupTables,
    pickupPartners,
    pickupAreas: newPickupAreas,
    routeInEditCode,
    selectedPostcodes,
    pickupToSelectedPostcodesByType,
  });

  return {
    pickupAreas: newPickupAreas,
    pickupModalConfig: newPickupModalConfig,
  };
};
